import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import Papa from 'papaparse';
import { importContactNotes } from '@/utils/zohoImport';

const ContactNotesImport = () => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
      toast.success(`${selectedFile.name} carregado`);
    }
  };

  // Parser manual de CSV para fallback
  const manualParseCSV = (text) => {
    console.log('🔧 Iniciando parser manual...');
    const lines = [];
    let currentLine = [];
    let currentField = '';
    let inQuotes = false;
    let charCount = 0;
    
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];
      charCount++;
      
      if (inQuotes) {
        if (char === '"' && nextChar === '"') {
          // Aspas escapadas ""
          currentField += '"';
          i++; // Pula próximo char
        } else if (char === '"') {
          // Fim das aspas
          inQuotes = false;
        } else {
          currentField += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ',') {
          currentLine.push(currentField);
          currentField = '';
        } else if (char === '\n' || (char === '\r' && nextChar === '\n')) {
          if (char === '\r') i++; // Pula \n em \r\n
          currentLine.push(currentField);
          lines.push(currentLine);
          currentLine = [];
          currentField = '';
          
          if (lines.length % 1000 === 0) {
            console.log(`📖 Parser manual: ${lines.length} linhas lidas...`);
          }
        } else {
          currentField += char;
        }
      }
    }
    
    // Última linha
    if (currentField || currentLine.length > 0) {
      currentLine.push(currentField);
      lines.push(currentLine);
    }
    
    console.log(`✅ Parser manual: ${lines.length} linhas, ${charCount} caracteres processados`);
    
    if (lines.length === 0) return [];
    
    // Converter para objetos
    const headers = lines[0].map(h => h.trim());
    const data = [];
    
    for (let i = 1; i < lines.length; i++) {
      const row = {};
      for (let j = 0; j < headers.length; j++) {
        row[headers[j]] = lines[i][j] || '';
      }
      // Filtrar linhas completamente vazias
      const hasContent = Object.values(row).some(val => val && val.trim());
      if (hasContent) {
        data.push(row);
      }
    }
    
    console.log(`✅ Parser manual resultou em ${data.length} registros válidos`);
    return data;
  };

  const parseCSV = (file) => {
    return new Promise((resolve, reject) => {
      console.log('🔍 Iniciando parsing do arquivo (3 camadas):', {
        nome: file.name,
        tamanho: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
        tipo: file.type,
      });

      // CAMADA 1: Papa Parse Otimizado
      let linesProcessed = 0;
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true, // Mais conservador que 'greedy'
        transformHeader: (header) => header.trim(),
        encoding: 'UTF-8',
        dynamicTyping: false,
        quotes: true,
        quoteChar: '"',
        escapeChar: '"',
        comments: false,
        
        // CAMADA 2: Logging Detalhado
        step: (results, parser) => {
          linesProcessed++;
          if (linesProcessed % 1000 === 0) {
            console.log(`📄 Papa Parse: ${linesProcessed} linhas processadas...`);
          }
        },
        
        complete: (results) => {
          console.log('📊 Papa Parse completo:', {
            totalLinhas: results.data.length,
            errosDeParseamento: results.errors.length,
            tamanhoArquivo: file.size,
            percentualLido: ((linesProcessed / 9346) * 100).toFixed(2) + '%',
            primeiraLinha: results.data[0],
            ultimaLinha: results.data[results.data.length - 1],
            headers: Object.keys(results.data[0] || {}),
          });
          
          // Log TODOS os erros
          if (results.errors.length > 0) {
            console.warn(`⚠️ ${results.errors.length} erros no parseamento:`);
            results.errors.forEach((error, idx) => {
              if (idx < 50) { // Log primeiros 50 erros
                console.warn(`  Erro ${idx + 1} [linha ${error.row}]:`, {
                  tipo: error.type,
                  codigo: error.code,
                  mensagem: error.message,
                });
              }
            });
            if (results.errors.length > 50) {
              console.warn(`  ... e mais ${results.errors.length - 50} erros`);
            }
          }
          
          // CAMADA 3: Fallback Manual se Papa Parse falhar
          if (results.data.length < 9000) {
            console.warn(`⚠️ Papa Parse leu apenas ${results.data.length} linhas de ~9,346 esperadas`);
            console.warn('🔄 Ativando fallback: Parser manual...');
            
            // Ler arquivo como texto e parsear manualmente
            const reader = new FileReader();
            reader.onload = (e) => {
              try {
                const text = e.target.result;
                console.log(`📖 Arquivo lido: ${text.length} caracteres, ${text.split('\n').length} quebras de linha`);
                
                const manualData = manualParseCSV(text);
                
                console.log('✅ Parser manual concluído:', {
                  linhasParseadas: manualData.length,
                  primeiraLinha: manualData[0],
                  ultimaLinha: manualData[manualData.length - 1],
                });
                
                if (manualData.length >= 9000) {
                  console.log('✅ Parser manual SUCESSO: Todas as ~9,346 linhas lidas!');
                  resolve(manualData);
                } else {
                  console.warn(`⚠️ Parser manual também leu apenas ${manualData.length} linhas`);
                  // Usar o que tiver mais linhas
                  resolve(manualData.length > results.data.length ? manualData : results.data);
                }
              } catch (error) {
                console.error('❌ Erro no parser manual:', error);
                // Fallback para resultado do Papa Parse
                console.warn('⚠️ Usando resultado do Papa Parse apesar de incompleto');
                resolve(results.data);
              }
            };
            reader.onerror = () => {
              console.error('❌ Erro ao ler arquivo para parser manual');
              resolve(results.data);
            };
            reader.readAsText(file, 'UTF-8');
          } else {
            console.log(`✅ Papa Parse SUCESSO: ${results.data.length} linhas lidas`);
            resolve(results.data);
          }
        },
        
        error: (error) => {
          console.error('❌ Erro fatal no Papa Parse:', error);
          reject(error);
        },
      });
    });
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Por favor, carregue um arquivo CSV');
      return;
    }

    setImporting(true);
    setBatchProgress({ current: 0, total: 0 });

    try {
      toast.info('Parseando CSV de notas de contatos...');
      const data = await parseCSV(file);
      
      console.log(`📋 Dados parseados do CSV:`, {
        totalLinhas: data.length,
        primeiroRegistro: data[0],
        campos: Object.keys(data[0] || {}),
      });
      
      // Validação dos campos necessários (mais flexível - ignora case e espaços)
      const actualFields = Object.keys(data[0] || {});
      const normalizeFieldName = (name) => name.toLowerCase().trim().replace(/\s+/g, ' ');
      
      const requiredFields = [
        { original: 'Record Id', normalized: 'record id', aliases: ['recordid', 'record_id', 'id'] },
        { original: 'Parent ID.id', normalized: 'parent id.id', aliases: ['parent id', 'parentid', 'parent_id'] },
        { original: 'Note Content', normalized: 'note content', aliases: ['notecontent', 'note_content', 'content'] },
      ];
      
      const fieldMapping = {};
      const missingFields = [];
      
      requiredFields.forEach(required => {
        const found = actualFields.find(field => {
          const normalized = normalizeFieldName(field);
          return normalized === required.normalized || 
                 required.aliases.some(alias => normalized === alias || normalized.includes(alias));
        });
        
        if (found) {
          fieldMapping[required.original] = found;
          console.log(`✅ Campo mapeado: "${required.original}" → "${found}"`);
        } else {
          missingFields.push(required.original);
        }
      });
      
      if (missingFields.length > 0) {
        console.error('❌ Campos disponíveis no CSV:', actualFields);
        console.error('❌ Campos ausentes:', missingFields);
        throw new Error(`Campos ausentes no CSV: ${missingFields.join(', ')}. Campos disponíveis: ${actualFields.join(', ')}`);
      }
      
      console.log('✅ Todos os campos necessários foram encontrados:', fieldMapping);
      
      // Estatísticas pré-importação (usando fieldMapping)
      const parentIdField = fieldMapping['Parent ID.id'];
      const noteContentField = fieldMapping['Note Content'];
      
      const uniqueParentIds = new Set(data.map(row => row[parentIdField]).filter(Boolean));
      console.log(`📊 Pré-validação:`, {
        totalLinhas: data.length,
        uniqueParentIds: uniqueParentIds.size,
        linhasSemParentId: data.filter(row => !row[parentIdField]).length,
        linhasSemContent: data.filter(row => !row[noteContentField]).length,
      });
      
      toast.info(`Importando ${data.length} notas de contatos (${uniqueParentIds.size} contatos únicos)...`);
      
      // Normalizar dados para usar nomes de campos padrão
      const normalizedData = data.map(row => ({
        'Record Id': row[fieldMapping['Record Id']],
        'Parent ID.id': row[fieldMapping['Parent ID.id']],
        'Note Content': row[fieldMapping['Note Content']],
        // Preservar outros campos que possam existir
        ...Object.keys(row).reduce((acc, key) => {
          if (!Object.values(fieldMapping).includes(key)) {
            acc[key] = row[key];
          }
          return acc;
        }, {}),
      }));
      
      console.log('✅ Dados normalizados:', {
        total: normalizedData.length,
        exemplo: normalizedData[0],
      });
      
      // Callback de progresso
      const progressCallback = (current, total) => {
        setBatchProgress({ current, total });
      };
      
      const importResult = await importContactNotes(normalizedData, progressCallback);
      
      setResult(importResult);
      toast.success('Importação de notas de contatos concluída!');
      
    } catch (error) {
      console.error('Erro na importação:', error);
      toast.error(`Erro: ${error.message}`);
    } finally {
      setImporting(false);
      setBatchProgress({ current: 0, total: 0 });
    }
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setBatchProgress({ current: 0, total: 0 });
  };

  const progressPercentage = batchProgress.total > 0 
    ? Math.round((batchProgress.current / batchProgress.total) * 100) 
    : 0;

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Importação de Notas de Contatos</h1>
        <p className="text-muted-foreground">
          Importe o arquivo CSV com as notas dos contatos do Zoho CRM
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Upload do Arquivo CSV</CardTitle>
          <CardDescription>
            Selecione o arquivo Notes_Contacts_2025_11_10-2.csv exportado do Zoho CRM
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!result ? (
            <>
              <label className="flex items-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary transition-colors">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={importing}
                />
                {file ? (
                  <>
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <div className="flex-1">
                      <p className="font-medium">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    <span>Carregar arquivo CSV de notas de contatos</span>
                  </>
                )}
              </label>

              {importing && batchProgress.total > 0 && (
                <Alert className="border-primary">
                  <AlertDescription>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Processando notas em lotes...</span>
                        <span className="text-lg font-bold text-primary">
                          {batchProgress.current.toLocaleString()} / {batchProgress.total.toLocaleString()}
                        </span>
                      </div>
                      <Progress 
                        value={progressPercentage} 
                        className="h-3"
                      />
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>{progressPercentage}% completo</span>
                        <span>
                          {batchProgress.total - batchProgress.current > 0 
                            ? `${(batchProgress.total - batchProgress.current).toLocaleString()} restantes` 
                            : 'Finalizando...'}
                        </span>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              <Alert>
                <AlertDescription>
                  <p className="font-semibold mb-2">ℹ️ Informações importantes:</p>
                  <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
                    <li>O arquivo deve estar no formato CSV exportado do Zoho CRM</li>
                    <li>As notas serão vinculadas aos contatos existentes pelo Zoho Contact ID</li>
                    <li>Notas duplicadas (mesmo Zoho Note ID) serão atualizadas automaticamente</li>
                    <li>O processamento é feito em lotes de 100 registros para maior eficiência</li>
                    <li>Notas sem contato associado serão ignoradas</li>
                  </ul>
                </AlertDescription>
              </Alert>

              <Button
                onClick={handleImport}
                disabled={importing || !file}
                className="w-full"
                size="lg"
              >
                {importing ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Importando notas de contatos...
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="mr-2 h-5 w-5" />
                    Iniciar Importação
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              {/* Results */}
              <div className="border-l-4 border-green-500 pl-4 bg-green-50 dark:bg-green-950 p-4 rounded">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold text-lg">Importação Concluída</h3>
                </div>
                
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-background rounded">
                    <div className="text-3xl font-bold text-green-600">{result.created}</div>
                    <div className="text-sm text-muted-foreground">Criadas</div>
                  </div>
                  <div className="text-center p-3 bg-background rounded">
                    <div className="text-3xl font-bold text-blue-600">{result.updated}</div>
                    <div className="text-sm text-muted-foreground">Atualizadas</div>
                  </div>
                  <div className="text-center p-3 bg-background rounded">
                    <div className="text-3xl font-bold text-gray-600">{result.skipped}</div>
                    <div className="text-sm text-muted-foreground">Ignoradas</div>
                  </div>
                </div>

                <div className="text-sm text-muted-foreground mb-4">
                  <p className="font-medium mb-1">Total processado: {result.created + result.updated + result.skipped} notas</p>
                  
                  {result.skipReasons && (
                    <div className="mt-3 p-3 bg-background rounded border">
                      <p className="font-semibold mb-2 text-foreground">📊 Motivos das notas ignoradas:</p>
                      <ul className="space-y-1 text-xs">
                        <li>🔴 Sem Parent ID: <strong>{result.skipReasons.noParentId}</strong></li>
                        <li>🟠 Contato não encontrado: <strong>{result.skipReasons.contactNotFound}</strong></li>
                        <li>🟡 Conteúdo vazio: <strong>{result.skipReasons.emptyContent}</strong></li>
                        <li>⚪ Dados inválidos: <strong>{result.skipReasons.invalidData}</strong></li>
                      </ul>
                      
                      {result.statistics && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="font-semibold mb-1 text-foreground">📈 Estatísticas:</p>
                          <ul className="space-y-1 text-xs">
                            <li>Total de linhas no CSV: <strong>{result.statistics.totalLinhas}</strong></li>
                            <li>Contatos únicos: <strong>{result.statistics.uniqueParentIds}</strong></li>
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {result.errors && result.errors.length > 0 && (
                  <Alert className="border-destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <p className="font-semibold mb-2">{result.errors.length} erros encontrados:</p>
                      <div className="max-h-48 overflow-y-auto">
                        <ul className="list-disc list-inside text-xs space-y-1">
                          {result.errors.slice(0, 10).map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                          {result.errors.length > 10 && (
                            <li className="font-semibold text-destructive">
                              ... e mais {result.errors.length - 10} erros
                            </li>
                          )}
                        </ul>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              <Button
                onClick={handleReset}
                variant="outline"
                className="w-full"
                size="lg"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Fazer Nova Importação
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Instructions Card */}
      <Card>
        <CardHeader>
          <CardTitle>Como usar</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
              1
            </div>
            <div>
              <p className="font-medium">Exporte as notas de contatos do Zoho CRM</p>
              <p className="text-sm text-muted-foreground">
                Vá em Notes no Zoho CRM e exporte como CSV (filtrado por "Parent Module = Contacts")
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
              2
            </div>
            <div>
              <p className="font-medium">Faça upload do arquivo CSV</p>
              <p className="text-sm text-muted-foreground">
                Clique na área de upload acima e selecione o arquivo Notes_Contacts.csv
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
              3
            </div>
            <div>
              <p className="font-medium">Inicie a importação</p>
              <p className="text-sm text-muted-foreground">
                Clique em "Iniciar Importação" e acompanhe o progresso em tempo real
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ContactNotesImport;
